import { useState } from "react";
import { DialogActions, DialogContent, DialogProps } from "@mui/material";
import { StyledDialog } from "src/components/modal/senec-dialog/SenecDialog.styled";
import { OnboardingStep } from "src/components/modal/senec-onboarding-dialog/commonTypes";
import { OnboardingDialogStep } from "src/components/modal/senec-onboarding-dialog/OnboardingDialogStep";
import { OnboardingDialogStepper } from "src/components/modal/senec-onboarding-dialog/OnboardingDialogStepper";
import { StepperButtons } from "src/components/navigation/stepper-buttons/SenecStepperButtons";

export interface SenecOnboardingDialogProps extends Omit<DialogProps, "open"> {
    /**
     * each step will be rendered as a dialog step.
     */
    onboardingSteps: OnboardingStep[];

    /**
     * open or close the dialog
     */
    isOpen: boolean;

    /**
     * callback function that gets called when the component is closed
     */
    onClose: () => void;

    /**
     * callback function that is triggered in the last step if onboarding is uncompleted
     */
    onSubmit: () => void;

    /**
     * label for bottom skip button
     */
    skipButtonLabel: string;

    /**
     * label for bottom start button
     */
    startButtonLabel: string;

    /**
     * label for bottom finish button
     */
    finishButtonLabel: string;

    /**
     * Skip button triggers onSubmit on true
     * @default: false
     */
    submitOnSkip?: boolean;

    /**
     * stepMinHeight - min height of the content to prevent the dialog to have "big jumps" when switch between steps.
     * @default "270px"
     */
    stepMinHeight?: string;

    /**
     * id to find component in render tests
     * @default "onboarding-dialog"
     */
    "data-testid"?: string;
}

/**
 * This component renders a multistep dialog for onboarding users and introducing them to new features.
 * Each step is rendered as a dialog step, providing detailed information or instructions.
 *
 * **Important Note:** This component does not have a functional mobile view. Ensure it is used only in desktop, tablet or larger screen contexts.
 */
export const SenecOnboardingDialog = ({
    onboardingSteps,
    isOpen,
    onClose,
    onSubmit,
    skipButtonLabel,
    startButtonLabel,
    finishButtonLabel,
    submitOnSkip = false,
    stepMinHeight = "270px",
    "data-testid": dataTestId = "onboarding-dialog",
}: SenecOnboardingDialogProps) => {
    const [activeStep, setActiveStep] = useState(0);

    const handleCloseDialog = () => {
        onClose();
        setActiveStep(0);
    };

    const handleComplete = () => {
        onSubmit();
        handleCloseDialog();
    };

    const buttons: StepperButtons = {
        skip: {
            label: skipButtonLabel,
            handleClick: submitOnSkip ? handleComplete : handleCloseDialog,
        },
        start: {
            label: startButtonLabel,
        },
        finish: {
            label: finishButtonLabel,
            handleClick: handleComplete,
        },
    };

    return (
        <StyledDialog data-testid={dataTestId} open={isOpen} onClose={handleCloseDialog} sx={{ zIndex: 100000 }}>
            <DialogContent sx={{ px: 3, pt: 3, pb: 0, "-ms-overflow-style": "none" }}>
                <OnboardingDialogStep step={onboardingSteps[activeStep]} stepMinHeight={stepMinHeight} />
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 2 }}>
                <OnboardingDialogStepper
                    totalSteps={onboardingSteps.length}
                    activeStep={activeStep}
                    setActiveStep={setActiveStep}
                    buttons={buttons}
                />
            </DialogActions>
        </StyledDialog>
    );
};
